import JSZip from "jszip";
import { Team } from "msd-capacity-planning-model";

export interface TeamDeltaExportSnapshot {
  getPlannedHours(): number;
  getCapacity(now: Date): number;
  getDelta(now: Date): number;
  getDeltaPercent(now: Date): number;
  getTimeoffHours(): number;
  countEngineers(): number;
  countCustomers(): number;
  countCustomersOffboarded(): number;
}

interface TeamDeltaExportMonth {
  label: string;
  capacity: number;
  planned: number;
  delta: number;
  deltaPercent: number | null;
  deltaRatio: number | null;
  engineers: number;
  customers: number;
  offboardings: number;
  ptoHours: number;
}

interface TeamDeltaExportRow {
  teamName: string;
  regionName: string;
  types: string;
  productTypes: string;
  months: TeamDeltaExportMonth[];
}

interface TeamDeltaExportColumn {
  field?:
    | "teamName"
    | "regionName"
    | "types"
    | "productTypes"
    | "capacity"
    | "planned"
    | "engineers"
    | "customers"
    | "offboardings"
    | "ptoHours";
  cellStyle?: "default" | "delta" | "percent";
  header: string;
  headerStyle?: "default" | "delta" | "percent";
  kind: "text" | "number" | "deltaFormula" | "percentFormula";
  monthIndex?: number;
  width: number;
}

export interface TeamDeltaExportOptions {
  dates: Date[];
  exportTeams: Team[];
  nowDate: Date;
  regions: { [regionId: string]: { name?: string } };
  showCustomers: boolean;
  showEngineers: boolean;
  showOffboardings: boolean;
  showProductTypes: boolean;
  showTimeOff: boolean;
  snapshots: { [date: string]: { [teamId: string]: TeamDeltaExportSnapshot } };
}

function buildExportRows(options: TeamDeltaExportOptions): TeamDeltaExportRow[] {
  const { dates, exportTeams, nowDate, regions, snapshots } = options;

  return exportTeams.map((team) => {
    const months = dates.map((date) => {
      const snapshot = snapshots[date.toISOString().substring(0, 7)]?.[team.id];
      const capacity = Math.round(snapshot?.getCapacity(nowDate) || 0);
      const planned = Math.round(snapshot?.getPlannedHours() || 0);
      const delta = capacity - planned;
      const deltaRatio = capacity === 0 ? null : planned / capacity;
      const deltaPercent =
        deltaRatio === null ? null : Number((deltaRatio * 100).toFixed(1));

      return {
        label: date.toISOString().substring(0, 7),
        capacity,
        planned,
        delta,
        deltaPercent,
        deltaRatio,
        engineers: snapshot?.countEngineers() || 0,
        customers: snapshot?.countCustomers() || 0,
        offboardings: snapshot?.countCustomersOffboarded() || 0,
        ptoHours: Math.round(snapshot?.getTimeoffHours() || 0),
      };
    });

    return {
      teamName: team.name,
      regionName: team.region ? regions[team.region]?.name || "" : "",
      types: (team.type || []).join(", "),
      productTypes: (team.productTypes || []).join(", "),
      months,
    };
  });
}

function escapeCsvValue(value: string | number): string {
  const stringValue = String(value ?? "");
  if (
    stringValue.includes(",") ||
    stringValue.includes("\"") ||
    stringValue.includes("\n")
  ) {
    return `"${stringValue.replace(/"/g, "\"\"")}"`;
  }
  return stringValue;
}

function escapeXml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/\"/g, "&quot;")
    .replace(/'/g, "&apos;");
}

function columnName(index: number): string {
  let name = "";
  let current = index;

  while (current > 0) {
    const remainder = (current - 1) % 26;
    name = String.fromCharCode(65 + remainder) + name;
    current = Math.floor((current - 1) / 26);
  }

  return name;
}

function sanitizeSheetName(name: string): string {
  const sanitized = name.replace(/[\\/?*\[\]:]/g, " ").trim();
  return (sanitized || "Export").substring(0, 31);
}

function downloadBlob(filename: string, blob: Blob): void {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

function buildCsvHeaders(options: TeamDeltaExportOptions): string[] {
  const {
    dates,
    showCustomers,
    showEngineers,
    showOffboardings,
    showProductTypes,
    showTimeOff,
  } = options;
  const headers = ["Team", "Region", "Types"];
  if (showProductTypes) headers.push("Product Types");

  dates.forEach((date) => {
    const monthLabel = date.toISOString().substring(0, 7);
    headers.push(`${monthLabel} Delta`);
    headers.push(`${monthLabel} Delta %`);
    if (showEngineers) headers.push(`${monthLabel} Engineers`);
    if (showCustomers) headers.push(`${monthLabel} Customers`);
    if (showOffboardings) headers.push(`${monthLabel} Offboardings`);
    if (showTimeOff) headers.push(`${monthLabel} PTO Hours`);
  });

  return headers;
}

function buildCsvRow(
  row: TeamDeltaExportRow,
  options: TeamDeltaExportOptions
): Array<string | number> {
  const {
    showCustomers,
    showEngineers,
    showOffboardings,
    showProductTypes,
    showTimeOff,
  } = options;
  const csvRow: Array<string | number> = [
    row.teamName,
    row.regionName,
    row.types,
  ];
  if (showProductTypes) csvRow.push(row.productTypes);

  row.months.forEach((month) => {
    csvRow.push(month.delta);
    csvRow.push(month.deltaPercent ?? "");
    if (showEngineers) csvRow.push(month.engineers);
    if (showCustomers) csvRow.push(month.customers);
    if (showOffboardings) csvRow.push(month.offboardings);
    if (showTimeOff) csvRow.push(month.ptoHours);
  });

  return csvRow;
}

function buildXlsxColumns(options: TeamDeltaExportOptions): TeamDeltaExportColumn[] {
  const {
    dates,
    showCustomers,
    showEngineers,
    showOffboardings,
    showProductTypes,
    showTimeOff,
  } = options;

  const columns: TeamDeltaExportColumn[] = [
    {
      field: "teamName",
      header: "Team",
      headerStyle: "default",
      kind: "text",
      width: 24,
    },
    {
      field: "regionName",
      header: "Region",
      headerStyle: "default",
      kind: "text",
      width: 18,
    },
    {
      field: "types",
      header: "Types",
      headerStyle: "default",
      kind: "text",
      width: 20,
    },
  ];

  if (showProductTypes) {
    columns.push({
      field: "productTypes",
      header: "Product Types",
      headerStyle: "default",
      kind: "text",
      width: 24,
    });
  }

  dates.forEach((date, monthIndex) => {
    const label = date.toISOString().substring(0, 7);
    columns.push(
      {
        field: "capacity",
        header: `${label} Capacity`,
        headerStyle: "default",
        kind: "number",
        monthIndex,
        width: 14,
      },
      {
        field: "planned",
        header: `${label} Planned`,
        headerStyle: "default",
        kind: "number",
        monthIndex,
        width: 14,
      },
      {
        cellStyle: "delta",
        header: `${label} Delta`,
        headerStyle: "delta",
        kind: "deltaFormula",
        monthIndex,
        width: 12,
      },
      {
        cellStyle: "percent",
        header: `${label} Delta %`,
        headerStyle: "percent",
        kind: "percentFormula",
        monthIndex,
        width: 12,
      }
    );

    if (showEngineers) {
      columns.push({
        cellStyle: "default",
        field: "engineers",
        header: `${label} Engineers`,
        headerStyle: "default",
        kind: "number",
        monthIndex,
        width: 12,
      });
    }
    if (showCustomers) {
      columns.push({
        cellStyle: "default",
        field: "customers",
        header: `${label} Customers`,
        headerStyle: "default",
        kind: "number",
        monthIndex,
        width: 12,
      });
    }
    if (showOffboardings) {
      columns.push({
        cellStyle: "default",
        field: "offboardings",
        header: `${label} Offboardings`,
        headerStyle: "default",
        kind: "number",
        monthIndex,
        width: 14,
      });
    }
    if (showTimeOff) {
      columns.push({
        cellStyle: "default",
        field: "ptoHours",
        header: `${label} PTO Hours`,
        headerStyle: "default",
        kind: "number",
        monthIndex,
        width: 12,
      });
    }
  });

  return columns;
}

function buildWorksheetXml(
  columns: TeamDeltaExportColumn[],
  rows: TeamDeltaExportRow[]
): string {
  const monthSupportRefs = columns.reduce((refs, column, index) => {
    if (
      column.kind === "number" &&
      column.monthIndex !== undefined &&
      (column.field === "capacity" || column.field === "planned")
    ) {
      refs[column.monthIndex] = refs[column.monthIndex] || {};
      refs[column.monthIndex][column.field] = columnName(index + 1);
    }
    return refs;
  }, {} as { [monthIndex: number]: { capacity?: string; planned?: string } });

  const lastRow = rows.length + 1;
  const lastColumn = columnName(columns.length);

  const headerCells = columns
    .map((column, index) => {
      const ref = `${columnName(index + 1)}1`;
      const headerStyleId =
        column.headerStyle === "delta"
          ? 3
          : column.headerStyle === "percent"
            ? 4
            : 1;
      return `<c r="${ref}" t="inlineStr" s="${headerStyleId}"><is><t xml:space="preserve">${escapeXml(
        column.header
      )}</t></is></c>`;
    })
    .join("");

  const dataRowsXml = rows
    .map((row, rowIndex) => {
      const excelRow = rowIndex + 2;
      const rowCells = columns
        .map((column, columnIndex) => {
          const ref = `${columnName(columnIndex + 1)}${excelRow}`;
          if (column.kind === "text") {
            const value =
              row[
                column.field as
                  | "teamName"
                  | "regionName"
                  | "types"
                  | "productTypes"
              ];
            return `<c r="${ref}" t="inlineStr"><is><t xml:space="preserve">${escapeXml(
              value
            )}</t></is></c>`;
          }

          const month = row.months[column.monthIndex || 0];
          if (column.kind === "number") {
            const value =
              month[
                column.field as
                | "capacity"
                | "planned"
                | "engineers"
                | "customers"
                | "offboardings"
                | "ptoHours"
              ];
            const cellStyleId =
              column.cellStyle === "delta"
                ? 5
                : column.cellStyle === "percent"
                  ? 6
                  : 0;
            return `<c r="${ref}"${
              cellStyleId ? ` s="${cellStyleId}"` : ""
            }><v>${value}</v></c>`;
          }

          if (column.kind === "deltaFormula") {
            const monthRefs = monthSupportRefs[column.monthIndex || 0] || {};
            const capacityRef = `${monthRefs.capacity}${excelRow}`;
            const plannedRef = `${monthRefs.planned}${excelRow}`;
            return `<c r="${ref}" s="5"><f>${capacityRef}-${plannedRef}</f><v>${month.delta}</v></c>`;
          }

          const monthRefs = monthSupportRefs[column.monthIndex || 0] || {};
          const capacityRef = `${monthRefs.capacity}${excelRow}`;
          const plannedRef = `${monthRefs.planned}${excelRow}`;
          const cachedValue =
            month.deltaRatio === null ? "0" : String(month.deltaRatio);
          return `<c r="${ref}" s="6"><f>IF(${capacityRef}=0,0,${plannedRef}/${capacityRef})</f><v>${cachedValue}</v></c>`;
        })
        .join("");

      return `<row r="${excelRow}">${rowCells}</row>`;
    })
    .join("");

  const colsXml = columns
    .map(
      (column, index) =>
        `<col min="${index + 1}" max="${index + 1}" width="${column.width}" customWidth="1"/>`
    )
    .join("");

  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
  <dimension ref="A1:${lastColumn}${lastRow}"/>
  <sheetViews>
    <sheetView workbookViewId="0">
      <pane ySplit="1" topLeftCell="A2" activePane="bottomLeft" state="frozen"/>
    </sheetView>
  </sheetViews>
  <sheetFormatPr defaultRowHeight="15"/>
  <cols>${colsXml}</cols>
  <sheetData>
    <row r="1">${headerCells}</row>
    ${dataRowsXml}
  </sheetData>
  <autoFilter ref="A1:${lastColumn}${lastRow}"/>
</worksheet>`;
}

function buildStylesXml(): string {
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
  <numFmts count="1">
    <numFmt numFmtId="164" formatCode="0.0%"/>
  </numFmts>
  <fonts count="2">
    <font>
      <sz val="11"/>
      <name val="Calibri"/>
      <family val="2"/>
    </font>
    <font>
      <b/>
      <sz val="11"/>
      <name val="Calibri"/>
      <family val="2"/>
    </font>
  </fonts>
  <fills count="6">
    <fill><patternFill patternType="none"/></fill>
    <fill><patternFill patternType="gray125"/></fill>
    <fill>
      <patternFill patternType="solid">
        <fgColor rgb="FFC5CCF0"/>
        <bgColor indexed="64"/>
      </patternFill>
    </fill>
    <fill>
      <patternFill patternType="solid">
        <fgColor rgb="FFD7DDF4"/>
        <bgColor indexed="64"/>
      </patternFill>
    </fill>
    <fill>
      <patternFill patternType="solid">
        <fgColor rgb="FFE0E5F8"/>
        <bgColor indexed="64"/>
      </patternFill>
    </fill>
    <fill>
      <patternFill patternType="solid">
        <fgColor rgb="FFECEFFA"/>
        <bgColor indexed="64"/>
      </patternFill>
    </fill>
  </fills>
  <borders count="1">
    <border>
      <left/>
      <right/>
      <top/>
      <bottom/>
      <diagonal/>
    </border>
  </borders>
  <cellStyleXfs count="1">
    <xf numFmtId="0" fontId="0" fillId="0" borderId="0"/>
  </cellStyleXfs>
  <cellXfs count="7">
    <xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/>
    <xf numFmtId="0" fontId="1" fillId="0" borderId="0" xfId="0" applyFont="1"/>
    <xf numFmtId="164" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/>
    <xf numFmtId="0" fontId="1" fillId="2" borderId="0" xfId="0" applyFont="1" applyFill="1"/>
    <xf numFmtId="0" fontId="1" fillId="3" borderId="0" xfId="0" applyFont="1" applyFill="1"/>
    <xf numFmtId="0" fontId="0" fillId="4" borderId="0" xfId="0" applyFill="1"/>
    <xf numFmtId="164" fontId="0" fillId="5" borderId="0" xfId="0" applyNumberFormat="1" applyFill="1"/>
  </cellXfs>
  <cellStyles count="1">
    <cellStyle name="Normal" xfId="0" builtinId="0"/>
  </cellStyles>
</styleSheet>`;
}

function buildWorkbookXml(sheetName: string): string {
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
  <sheets>
    <sheet name="${escapeXml(sheetName)}" sheetId="1" r:id="rId1"/>
  </sheets>
</workbook>`;
}

function buildWorkbookRelsXml(): string {
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/>
  <Relationship Id="rId2" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>
</Relationships>`;
}

function buildRootRelsXml(): string {
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>
  <Relationship Id="rId2" Type="http://schemas.openxmlformats.org/package/2006/relationships/metadata/core-properties" Target="docProps/core.xml"/>
  <Relationship Id="rId3" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/extended-properties" Target="docProps/app.xml"/>
</Relationships>`;
}

function buildContentTypesXml(): string {
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
  <Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
  <Default Extension="xml" ContentType="application/xml"/>
  <Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>
  <Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>
  <Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>
  <Override PartName="/docProps/core.xml" ContentType="application/vnd.openxmlformats-package.core-properties+xml"/>
  <Override PartName="/docProps/app.xml" ContentType="application/vnd.openxmlformats-officedocument.extended-properties+xml"/>
</Types>`;
}

function buildCoreXml(): string {
  const timestamp = new Date().toISOString();
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<cp:coreProperties xmlns:cp="http://schemas.openxmlformats.org/package/2006/metadata/core-properties" xmlns:dc="http://purl.org/dc/elements/1.1/" xmlns:dcterms="http://purl.org/dc/terms/" xmlns:dcmitype="http://purl.org/dc/dcmitype/" xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance">
  <dc:creator>Codex</dc:creator>
  <cp:lastModifiedBy>Codex</cp:lastModifiedBy>
  <dcterms:created xsi:type="dcterms:W3CDTF">${timestamp}</dcterms:created>
  <dcterms:modified xsi:type="dcterms:W3CDTF">${timestamp}</dcterms:modified>
</cp:coreProperties>`;
}

function buildAppXml(): string {
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Properties xmlns="http://schemas.openxmlformats.org/officeDocument/2006/extended-properties" xmlns:vt="http://schemas.openxmlformats.org/officeDocument/2006/docPropsVTypes">
  <Application>Microsoft Excel</Application>
</Properties>`;
}

export function downloadTeamDeltaCsv(
  filename: string,
  options: TeamDeltaExportOptions
): void {
  const headers = buildCsvHeaders(options);
  const rows = buildExportRows(options).map((row) => buildCsvRow(row, options));
  const csv = [headers, ...rows]
    .map((row) => row.map((value) => escapeCsvValue(value)).join(","))
    .join("\n");

  downloadBlob(
    filename,
    new Blob(["\uFEFF" + csv], {
      type: "text/csv;charset=utf-8;",
    })
  );
}

export async function downloadTeamDeltaXlsx(
  filename: string,
  sheetName: string,
  options: TeamDeltaExportOptions
): Promise<void> {
  const zip = new JSZip();
  const columns = buildXlsxColumns(options);
  const rows = buildExportRows(options);
  const sanitizedSheetName = sanitizeSheetName(sheetName);

  zip.file("[Content_Types].xml", buildContentTypesXml());
  zip.folder("_rels")?.file(".rels", buildRootRelsXml());
  zip.folder("docProps")?.file("core.xml", buildCoreXml());
  zip.folder("docProps")?.file("app.xml", buildAppXml());
  zip.folder("xl")?.file("workbook.xml", buildWorkbookXml(sanitizedSheetName));
  zip.folder("xl")?.file("styles.xml", buildStylesXml());
  zip
    .folder("xl")
    ?.folder("_rels")
    ?.file("workbook.xml.rels", buildWorkbookRelsXml());
  zip
    .folder("xl")
    ?.folder("worksheets")
    ?.file("sheet1.xml", buildWorksheetXml(columns, rows));

  const blob = await zip.generateAsync({
    mimeType:
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    type: "blob",
  });

  downloadBlob(filename, blob);
}
